import { FC, Fragment } from "react";
import { useDidMount } from "rooks";
import { Stack } from "@mui/material";
import { useFlag } from "@unleash/proxy-client-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useHover } from "@/utils/hooks/useHover.hook";
import { getNormalizedCostWidgetDataByWidgetIdThunk } from "@/store/dashboards/thunks/widgets/normalized-cost-widget/getNormalizedCostWidgetDataByWidgetIdThunk";
import { normalizedCostWidgetByWidgetIdLoadingSelector } from "@/store/dashboards/selectors/widgets/normalized-cost-widget/widget-view/normalizedCostWidgetByWidgetIdLoadingSelector";
import { WidgetLoadingState } from "@/components/pages/dashboard/components/widgets/common/WidgetLoadingState";
import { NormalizedCostWidgetCardHeader } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/components/header/NormalizedCostWidgetCardHeader";
import { NormalizedCostWidgetContent } from "@/components/pages/dashboard/components/widgets/normalized-cost-widget/components/NormalizedCostWidgetContent";
import { generateWidgetInnerContentId } from "@/components/pages/dashboard/utils/helpers/widget/generateWidgetInnerContentId";

interface DashboardNormalizedCostWidgetProps {
  widgetId: string;
}

export const DashboardNormalizedCostWidget: FC<
  DashboardNormalizedCostWidgetProps
> = ({ widgetId }) => {
  const dispatch = useAppDispatch();
  const { ref, hovered } = useHover();
  const enableNormalizedCostWidget = useFlag("EnableNormalizedCostWidget");

  const loading = useAppSelector((state) =>
    normalizedCostWidgetByWidgetIdLoadingSelector(state, widgetId),
  );

  useDidMount(() => {
    if (!enableNormalizedCostWidget) {
      return;
    }

    dispatch(getNormalizedCostWidgetDataByWidgetIdThunk({ widgetId }));
  });

  if (!enableNormalizedCostWidget) {
    return;
  }

  return (
    <Stack
      ref={ref}
      flexGrow={1}
      overflow="auto"
      id={generateWidgetInnerContentId(widgetId)}
    >
      {loading ? (
        <WidgetLoadingState />
      ) : (
        <Fragment>
          <NormalizedCostWidgetCardHeader
            widgetId={widgetId}
            hovered={hovered}
          />
          <NormalizedCostWidgetContent widgetId={widgetId} />
        </Fragment>
      )}
    </Stack>
  );
};
